import * as React from 'react'
import { TextField } from '../ui/TextField'
import { Button, ButtonText } from '../ui/button'
import { useController, useForm, useWatch } from 'react-hook-form'
import { useState } from 'react'
import useAppStore from '@/store/useAppStore'
import useEditTransactionCategory from '@/services/api/transaction-category/useEditTransactionCategory'
import NonModalWindow from '@/components/NonModalWindow'
import SimpleComboBox from '../SimpleComboBox'
import { Description } from '../ui/Description'
import useGetVaults from '@/services/api/vault/useGetVaults'
import _ from 'lodash'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import CloseConfirmationModal from '../CloseConfirmationModal'

export interface TransactionCategoryValues {
  name: string
  description?: string
  default_vault_id: any
}

export default function EditTransactionCategoryForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') //in|out

  const { categoryId } = useParams()

  const {
    data: editedEntity,
    isLoading: isLoadingCategory,
    refetch,
  } = useGetTransactionCategory({ id: categoryId })

  const [isFocusedName, setIsFocusedName] = useState(false)
  const [isFocusedDescription, setIsFocusedNameDescription] = useState(false)
  const [openCloseWarning, setOpenCloseWarning] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    setValue,
  } = useForm<TransactionCategoryValues>({
    defaultValues: async () => {
      const { data: editedEntity } = await refetch()
      return {
        name: editedEntity?.name,
        description: editedEntity?.description,
        default_vault_id: editedEntity?.default_vault_id,
      }
    },
  })
  // const nonModalWindowType = useAppStore((state) => state.nonModalWindowType)
  const toggleNonModalWindow = useAppStore(
    (state) => state.toggleNonModalWindowState,
  )

  const { editTransactionCategory } = useEditTransactionCategory(categoryId)
  const { data: vaults } = useGetVaults()

  const name = useWatch({
    control,
    name: 'name',
  })

  const description = useWatch({
    control,
    name: 'description',
  })

  const handleNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setValue('name', value)
    }
  }

  const handleDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setValue('description', value)
    }
  }

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      return vault.is_group ? vault.vaults : [vault]
    }),
  )

  const { field: defaultVaultField } = useController({
    control: control,
    name: 'default_vault_id',
    shouldUnregister: true,
  })

  const selectedVault = vaultsFlat.find(
    (x) => x.id === defaultVaultField?.value,
  )

  const onSubmit = async (data) => {
    await editTransactionCategory(data)
    navigate('/')
    toggleNonModalWindow()
  }

  return (
    <>
      <CloseConfirmationModal
        open={openCloseWarning}
        setOpen={setOpenCloseWarning}
        onConfirm={() => {
          setOpenCloseWarning(false)
          navigate('/')
          toggleNonModalWindow()
        }}
      />
      <form
        noValidate
        className=" flex flex-col"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const activeElement = document.activeElement as HTMLElement
            if (
              activeElement.tagName === 'INPUT' ||
              activeElement.tagName === 'TEXTAREA'
            ) {
              e.preventDefault()
            }
          }
        }}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(onSubmit)
        }}
      >
        <NonModalWindow
          isDirty={isDirty}
          isLoading={isLoadingCategory}
          onSubmit={handleSubmit(onSubmit)}
          title={
            (type === 'in' && 'Edit Income Category') ||
            (type === 'out' && 'Edit Expense Category')
          }
          onClose={() => {
            setOpenCloseWarning(true)
          }}
        >
          <NonModalWindow.Body className="min-h-[78vh]">
            <p className="mb-3 text-sm text-txt-secondary">General</p>
            <div className="mb-3">
              <TextField
                status={errors?.name ? 'error' : null}
                statusText={errors.name?.message}
              >
                <TextField.Label className="flex justify-between text-sm">
                  <span>Category Name</span>
                  <span
                    className={`text-txt-secondary ${isFocusedName ? '' : 'hidden'}`}
                  >
                    {name && name.length}/30
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={30}
                  placeholder="e.g., Revenue"
                  {...register('name', {
                    required: 'Please fill out this field',
                    value: name,
                    onChange: handleNameChange,
                  })}
                  onFocus={() => setIsFocusedName(true)}
                  onBlur={() => setIsFocusedName(false)}
                />
              </TextField>
            </div>
            <div className="mb-3">
              <TextField>
                <TextField.Label className="flex justify-between text-sm">
                  Description
                  <span
                    className={`text-txt-secondary ${isFocusedDescription ? '' : 'hidden'}`}
                  >
                    {description ? description.length : '0'}/40
                  </span>
                </TextField.Label>
                <TextField.Input
                  maxLength={40}
                  placeholder="e.g., Core Business"
                  {...register('description', {
                    value: description,
                    onChange: handleDescriptionChange,
                  })}
                  onFocus={() => setIsFocusedNameDescription(true)}
                  onBlur={() => setIsFocusedNameDescription(false)}
                />
              </TextField>
            </div>
            <div className="mb-4">
              <TextField>
                <TextField.Label className="text-sm">
                  Vault for Transactions
                </TextField.Label>
                <SimpleComboBox
                  placeholder="Use Default"
                  items={vaultsFlat}
                  selected={selectedVault}
                  setSelected={(item) =>
                    defaultVaultField.onChange(item?.id ?? null)
                  }
                  getOptionLabel={(item) => (
                    <div>
                      <p>{item.name}</p>
                      <Description>{item.description}</Description>
                    </div>
                  )}
                  getSelectedLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  clearable
                />
              </TextField>
            </div>
          </NonModalWindow.Body>
          <NonModalWindow.Footer>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={() => {
                if (isDirty) {
                  setOpenCloseWarning(true)
                } else {
                  navigate('/')
                  toggleNonModalWindow()
                }
              }}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              <ButtonText>Save</ButtonText>
            </Button>
          </NonModalWindow.Footer>
        </NonModalWindow>
      </form>
    </>
  )
}
